var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { initReactI18next, useTranslation as useTranslationOriginal } from 'react-i18next';
import axios from 'axios';
import { getAccessToken } from '@smile-next/api';
import { Configuration, UserControllerApi } from '@smile-next/api/lib/namelist';
import { translationResources, translationNamespace } from "../translationResources";
import { getEnv } from "../helpers/env-helper";
import { useAuth } from "./useAuth";
/**
 * useTranslationを生成する関数。
 *
 * - 文字列リソースを定義して、そのキーをGenericに渡す。
 * - uikitとの衝突回避のためnamespaceをアプリごとに定義する。
 * - namespaceは共通ライブラリ以外は基本的に`local`などでOK。
 */
export function createUseTranslationHook(defaultNamespace, defaultOptions) {
    return function useTranslation(namespace, options) {
        var _a = useTranslationOriginal(namespace !== null && namespace !== void 0 ? namespace : defaultNamespace, __assign(__assign({}, defaultOptions), options)), t = _a[0], i18n = _a[1];
        var nameDisplay = useContext(TranslationContext).nameDisplay;
        // i18n.changeLanguageは内部でthisを使っているためarrow関数で囲う。
        var changeLanguage = useCallback(function (lng) { return i18n.changeLanguage(lng); }, [i18n]);
        return {
            t: t,
            changeLanguage: changeLanguage,
            language: i18n.language,
            i18n: i18n,
            nameDisplay: nameDisplay,
        };
    };
}
/**
 * useTranslation hookを使うためのi18next初期化。
 *
 * アプリで一回だけ呼び出す。
 */
export function initializeTranslation(resources) {
    var _a;
    var _b;
    var resourceKeys = Object.keys(resources !== null && resources !== void 0 ? resources : {});
    i18next.use(initReactI18next).init({
        ns: __spreadArrays([translationNamespace], resourceKeys),
        defaultNS: (_b = resourceKeys[0]) !== null && _b !== void 0 ? _b : translationNamespace,
        resources: {
            'ja-JP-elementary': __assign((_a = {}, _a[translationNamespace] = translationResources, _a), resources),
            'ja-JP': {},
        },
        lng: 'ja-JP',
        fallbackLng: 'ja-JP',
        keySeparator: false,
        nsSeparator: false,
        interpolation: {
            escapeValue: false,
        },
    });
}
export var translationAxiosInstance = axios.create();
var env = getEnv();
/**
 * 認証情報を取得し言語（漢字ON/OFF）や名前の表示(名前/ふりがな)の切り替えを管理するContextProvider。
 *
 * AuthProviderより下で用いる。
 */
export var TranslationProvider = function (props) {
    var children = props.children;
    var user = useAuth().user;
    var changeLanguage = useTranslation().changeLanguage;
    var _a = useState(undefined), grade = _a[0], setGrade = _a[1];
    /**
     * roleとgradeで判定を行い、名前/ふりがなの出し分けの切り替えを行う
     */
    var nameDisplay = useMemo(function () {
        if (user == null || grade == null)
            return 'name';
        return user.userRole === 'STUDENT' && grade <= 2 ? 'ruby' : 'name';
    }, [grade, user]);
    useEffect(function () {
        try {
            if (user == null)
                return;
            var userApi = new UserControllerApi(new Configuration({ accessToken: function () { return getAccessToken(); } }), env.api.namelist, translationAxiosInstance);
            userApi.getCurrentUser().then(function (response) {
                if (response.data.user == null)
                    return;
                setGrade(response.data.user.grade);
            });
        }
        catch (e) {
            setGrade(undefined);
        }
    }, [user]);
    useEffect(function () {
        if (user == null)
            return;
        if (user.userRole === 'ADMINISTRATOR_TEACHER' || user.userRole === 'TEACHER') {
            changeLanguage('ja-JP');
            return;
        }
        // 以下 user.userRole === 'STUDENT'
        if (user.schoolType === 'MIDDLE_SCHOOL') {
            changeLanguage('ja-JP');
            return;
        }
        if (user.schoolType === 'ELEMENTARY_SCHOOL') {
            changeLanguage('ja-JP-elementary');
            return;
        }
        if (user.schoolType === 'COMPULSORY_EDUCATION_SCHOOL') {
            if (grade != null) {
                changeLanguage(grade <= 6 ? 'ja-JP-elementary' : 'ja-JP');
            }
            return;
        }
        console.error("unexpected user role: " + user.userRole + " schoolType: " + user.schoolType);
    }, [user, changeLanguage, grade]);
    return React.createElement(TranslationContext.Provider, { value: { nameDisplay: nameDisplay } }, children);
};
var initialState = {
    nameDisplay: 'name',
};
export var TranslationContext = React.createContext(initialState);
/**
 * UiKit内で用いるt関数を返すHook。
 */
export var useTranslation = createUseTranslationHook(translationNamespace);
